import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Quote, Star, Handshake } from "lucide-react";
import axios from "axios";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const videoTestimonials = [
  {
    id: "v1",
    name: "Neeko McLemore",
    title: "Feeding the Community",
    videoUrl: "https://www.facebook.com/reel/4468015963337156/",
  },
  {
    id: "v2",
    name: "Joshlilj",
    title: "Blessing a Man Get Shelter!",
    videoUrl: "https://www.facebook.com/reel/910528265134082",
  },
  {
    id: "v3",
    name: "Joshlilj",
    title: "Feeding the Homeless!",
    videoUrl: "https://www.facebook.com/reel/950639890963091",
  },
];

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testimonialsRes, partnersRes] = await Promise.all([
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/partners`),
        ]);
        setTestimonials(testimonialsRes.data);
        setPartners(partnersRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="testimonials-page" className="pt-20">
      <SEO
        title="Testimonials"
        description="Hear from youth, seniors, volunteers, and families whose lives have been touched by Confidence 2 Create. Watch video stories and read testimonials."
      />
      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/xitkw0t4_Seniors%204.jpg" 
            alt="Culinary Buddies" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block bg-[#FFC107] text-[#0033A0] px-4 py-2 rounded-full text-sm font-bold mb-6">
              Success Stories
            </span>
            <h1 className="text-3xl md:text-6xl font-black text-white mb-6">
              What Our <span className="text-[#FFC107]">Community</span> Says
            </h1>
            <p className="text-base md:text-xl text-white/90 max-w-2xl mx-auto">
              Hear from the youth, seniors, volunteers, and families whose lives have been touched by Confidence 2 Create.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      <section className="section-padding bg-white" data-testid="video-testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 md:mb-16"
          >
            <span className="text-[#FFC107] font-bold tracking-wide uppercase">Video Stories</span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0033A0] mt-4">
              Watch Our Community Stories
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
            {videoTestimonials.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`video-testimonial-${index}`}
              >
                <div className="rounded-2xl overflow-hidden shadow-lg bg-black max-h-[480px] sm:max-h-none" style={{ aspectRatio: "9/16" }}>
                  <iframe
                    src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(video.videoUrl)}&show_text=false&t=0`}
                    width="100%"
                    height="100%"
                    style={{ border: "none", overflow: "hidden" }}
                    scrolling="no"
                    frameBorder="0"
                    allowFullScreen={true}
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                    title={video.title}
                  />
                </div>
                <div className="mt-4">
                  <h3 className="text-xl font-bold text-[#0033A0]">{video.title}</h3>
                  <p className="text-slate-600">{video.name}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Written Testimonials Grid */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 md:mb-16"
          >
            <span className="text-[#FFC107] font-bold tracking-wide uppercase">Written Testimonials</span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0033A0] mt-4">
              Community Voices
            </h2>
          </motion.div>

          {loading ? (
            <div className="flex justify-center items-center py-20" data-testid="testimonials-loading">
              <div className="w-12 h-12 border-4 border-[#0033A0] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="testimonial-card bg-white p-6 md:p-8 rounded-2xl hover:shadow-lg transition-all"
                  data-testid={`testimonial-card-${index}`}
                >
                  <Quote className="w-10 h-10 text-[#FFC107] mb-4 opacity-50" />
                  
                  <p className="text-slate-700 leading-relaxed mb-6 italic">
                    "{testimonial.content}"
                  </p>

                  <div className="flex items-center gap-3 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className="fill-[#FFC107] text-[#FFC107]"
                      />
                    ))}
                  </div>

                  <div>
                    <p className="font-bold text-[#0033A0]">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Partnerships Section */}
      <section className="section-padding bg-white" data-testid="partnerships-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 md:mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <Handshake className="w-8 h-8 text-[#FFC107]" />
              <span className="text-[#FFC107] font-bold tracking-wide uppercase">Our Partners</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0033A0] mt-4 mb-4">
              Community Partnerships
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We're grateful for the organizations and businesses that support our mission to empower youth and strengthen our community.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {partners.map((partner, index) => (
              <motion.div
                key={partner.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-4 flex items-center justify-center h-28 hover:shadow-md transition-all border border-slate-200 hover:border-[#FFC107]"
                data-testid={`partner-${index}`}
              >
                {partner.logo_url ? (
                  <img src={partner.logo_url} alt={partner.name} className="max-h-20 max-w-full object-contain" />
                ) : (
                  <div className="text-center flex flex-col items-center justify-center gap-2">
                    <div className="w-10 h-10 bg-[#0033A0] rounded-lg flex items-center justify-center">
                      <Handshake className="w-5 h-5 text-[#FFC107]" />
                    </div>
                    <span className="text-[#0033A0] text-sm font-bold">{partner.name}</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-slate-600 mb-4">Interested in partnering with us?</p>
            <a
              href="/contact"
              data-testid="become-partner-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              <Handshake size={18} />
              Become a Partner
            </a>
          </motion.div>
        </div>
      </section>

      {/* Featured Quote */}
      <section className="section-padding bg-[#0033A0]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <Quote className="w-16 h-16 text-[#FFC107] mx-auto mb-8 opacity-50" />
            <blockquote className="text-2xl md:text-3xl text-white font-medium leading-relaxed mb-8">
              "The most powerful thing we can do is invest in our youth. When we teach them skills, we're not just preparing them for jobs—we're preparing them for life."
            </blockquote>
            <div className="flex items-center justify-center gap-4">
              <div className="w-16 h-16 bg-[#FFC107] rounded-full flex items-center justify-center">
                <span className="text-[#0033A0] font-black text-xl">NM</span>
              </div>
              <div className="text-left">
                <p className="text-white font-bold">Neeko McLemore</p>
                <p className="text-[#FFC107]">Founder & President</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Share Your Story CTA */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0033A0] mb-6">
              Share Your Story
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              Has Confidence 2 Create made a difference in your life or community? We'd love to hear from you!
            </p>
            <a
              href="/contact"
              data-testid="share-story-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              Contact Us to Share
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
